import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import {Link as ContentfulLink} from '@amblique/king-living/app/contentful/models/link';
import {
  Box, Popover, PopoverAnchor, PopoverContent, useMediaQuery, useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {ensureArray} from '@amblique/king-living/app/utils/utils';
import {MenuItemContent} from '@amblique/king-living/app/components/mega-menu/shared/menu-item-container';

const hasSubmenu = (menuItem) => {
  return menuItem?.submenuCollection?.items?.length > 0;
};

const POPOVER_MODIFIERS = [
  {
    name: 'offset',
    options: {
      offset: [0, 0],
    },
  },
  {
    name: 'computeStyles',
    options: {
      gpuAcceleration: false,
    },
  }
];

const MegaMenuTriggerComponent = ({
  isOpen = false,
  menuId,
  menuItem,
  navigationMenu,
  onClose,
  onForceClose,
  onOpen,
  onToggle,
  ...props
}) => {
  const [popoverTop, setPopoverTop] = useState(0);
  const [isDesktop] = useMediaQuery('(min-width: 992px)');
  const triggerRef = useRef(null);
  const headerWrapperRef = useRef(null);
  const styles = useMultiStyleConfig('MegaMenu', {isDesktop, isOpen, popoverTop});

  useEffect(() => {
    if (!isOpen || !isDesktop) {
      return;
    }

    const updatePopoverPosition = () => {
      const triggerButton = triggerRef.current;

      if (!headerWrapperRef.current) {
        headerWrapperRef.current = document.querySelector('[data-header-wrapper]');
      }
      const headerWrapper = headerWrapperRef.current;

      if (triggerButton) {
        // Find the parent nav element (megaMenuDesktopNav) which includes padding and border
        const navElement = triggerButton.closest('nav');

        if (navElement) {
          const navRect = navElement.getBoundingClientRect();

          // Nav bottom already includes paddingBottom (16px) and border (1px)
          // Add 2px for the active indicator
          setPopoverTop(navRect.bottom + 2);
        } else {
          // Fallback: trigger bottom + offset for padding (16px) + border (1px) + indicator (2px)
          const triggerRect = triggerButton.getBoundingClientRect();

          setPopoverTop(triggerRect.bottom + 19);
        }
      } else if (headerWrapper) {
        const height = headerWrapper.getBoundingClientRect().height;

        setPopoverTop(height);
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      updatePopoverPosition();
    }, 0);

    // Update on resize
    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition, true);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updatePopoverPosition);
      window.removeEventListener('scroll', updatePopoverPosition, true);
    };
  }, [isOpen, isDesktop, menuItem?.sys?.id]);

  // Global Escape key handler - close menu when Escape is pressed anywhere
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleGlobalKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onForceClose?.();
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [isOpen, onForceClose]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle?.(event);
    } else if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      onForceClose?.();
    }
  }, [onToggle, onForceClose, isOpen]);

  // Wrapper to set data attribute before calling parent handler
  const handleMouseEnter = useCallback((event) => {
    event.currentTarget.dataset.menuId = menuId;
    onOpen?.(event);
  }, [menuId, onOpen]);

  const submenuItems = ensureArray(menuItem?.submenuCollection?.items);
  const hasSubmenuItems = hasSubmenu(menuItem);
  const link = menuItem?.link;

  if (!hasSubmenuItems) {
    return (
      <Box {...props} data-menu-id={menuId} onMouseEnter={onClose}>
        <Box
          as="span"
          className={link ? 'menu-item-wrapper' : 'menu-item'}
          display={link ? 'inline-block' : undefined}
          position={link ? 'relative' : undefined}
          {...styles.desktopTriggerButton}
        >
          {link ? (
            <ContentfulLink
              className="menu-item"
              component={link}
              sx={{
                _hover: {
                  textDecoration: 'none !important', // Override ContentfulLink's underline hover
                },
              }}
            >
              {menuItem?.type === 'sale' && <Box as="span" {...styles.saleBadge} />}
              {menuItem.title}
            </ContentfulLink>
          ) : (
            <>
              {menuItem?.type === 'sale' && <Box as="span" {...styles.saleBadge} />}
              {menuItem.title}
            </>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box {...props}>
      <Popover
        closeOnBlur={false}
        isLazy={true}
        isOpen={isOpen}
        lazyBehavior="keepMounted"
        matchWidth={false}
        modifiers={POPOVER_MODIFIERS}
        placement="bottom-start"
        variant="fullWidth"
      >
        <PopoverAnchor>
          <Box
            ref={triggerRef}
            as="button"
            className="menu-item"
            data-menu-id={menuId}
            {...styles.desktopTriggerButton}
            onKeyDown={handleKeyDown}
            onMouseEnter={handleMouseEnter}
          >
            {menuItem?.type === 'sale' && (<Box as="span" {...styles.saleBadge} />)}
            {menuItem.title}
          </Box>
        </PopoverAnchor>
        <PopoverContent
          {...styles.desktopPopoverContent}
          data-menu-id={menuId}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={onClose}
        >
          <MenuItemContent isMobile={false} menuItem={menuItem} submenuItems={submenuItems} />
        </PopoverContent>
      </Popover>
    </Box>
  );
};

MegaMenuTriggerComponent.displayName = 'MegaMenuTrigger';
MegaMenuTriggerComponent.propTypes = {
  isOpen: PropTypes.bool,
  menuId: PropTypes.string.isRequired,
  menuItem: PropTypes.object.isRequired,
  navigationMenu: PropTypes.object,
  onClose: PropTypes.func,
  onForceClose: PropTypes.func,
  onOpen: PropTypes.func,
  onToggle: PropTypes.func,
};

/**
 * Memoized MegaMenuTrigger component.
 * Only re-renders when props actually change.
 *
 * Custom comparison function optimizes for:
 * - isOpen: boolean comparison (changes when menu opens/closes)
 * - menuId: string comparison (stable)
 * - menuItem: compares by sys.id to handle object reference changes
 * - Function props: reference comparison (all stable via useCallback)
 * - navigationMenu: reference comparison (stable)
 */
export const MegaMenuTrigger = React.memo(MegaMenuTriggerComponent, (prevProps, nextProps) => {
  // Quick reference equality check for all props
  if (
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.menuId === nextProps.menuId &&
    prevProps.menuItem?.sys?.id === nextProps.menuItem?.sys?.id &&
    prevProps.navigationMenu === nextProps.navigationMenu &&
    prevProps.onClose === nextProps.onClose &&
    prevProps.onForceClose === nextProps.onForceClose &&
    prevProps.onOpen === nextProps.onOpen &&
    prevProps.onToggle === nextProps.onToggle
  ) {
    // Props are equal, skip re-render
    return true;
  }

  // Props changed, allow re-render
  return false;
});
